// Builds Cloudinary delivery URLs directly so the component renders a complete
// <img src/srcSet> during server rendering (no browser-only Cloudinary SDK).
// `f_auto,q_auto` lets Cloudinary pick format/quality; `c_limit` scales down
// but never upscales beyond the original asset.

// A Cloudinary transformation component as an object, e.g.
// { c: 'fill', g: 'auto', ar: '4:3' } serializes to "c_fill,g_auto,ar_4:3"
export type CloudinaryTransformations = { [key: string]: string | number };

// Covers phone through large desktop displays, including 2x DPR on common
// viewport widths. Upper bound keeps multi-thousand-pixel originals in check.
export const cloudinarySrcSetWidths = [480, 768, 1080, 1600, 2400];

// Mid-size fallback for browsers/tools that ignore srcset.
const fallbackSrcWidth = 1080;

const defaultTransformations: CloudinaryTransformations = {
  f: 'auto',
  q: 'auto',
  c: 'limit',
};

function serializeTransformations(transformations: CloudinaryTransformations): string {
  return Object.entries(transformations)
    .map(([key, value]) => `${key}_${value}`)
    .join(',');
}

// Accepts the raw Cloudinary string form ("c_fill,g_auto,ar_4:3") and parses
// it into an object so it merges over the defaults the same way object input
// does (e.g. a user c_fill cleanly replaces the default c_limit)
function parseTransformationString(value: string): CloudinaryTransformations {
  const result: CloudinaryTransformations = {};
  value.split(',').forEach((entry) => {
    const trimmed = entry.trim();
    if (!trimmed) {
      return;
    }
    const separatorIndex = trimmed.indexOf('_');
    if (separatorIndex <= 0) {
      return;
    }
    result[trimmed.slice(0, separatorIndex)] = trimmed.slice(separatorIndex + 1);
  });
  return result;
}

function buildCloudinaryImageUrl(
  cloudName: string,
  publicId: string,
  width: number,
  transformations?: CloudinaryTransformations | string
): string {
  const userTransformations =
    typeof transformations === 'string'
      ? parseTransformationString(transformations)
      : transformations;

  // User keys override defaults (object spread = "split the object onto the
  // configuration"). `w` is always component-managed so srcset width variants
  // stay correct; combined with user ar/c/g values Cloudinary produces
  // consistent crops at every width.
  const merged: CloudinaryTransformations = {
    ...defaultTransformations,
    ...userTransformations,
    w: width,
  };

  // encodeURI preserves the slashes that are part of Cloudinary public IDs
  // (e.g. "images/<uuid>/<id>") while escaping unsafe characters
  return encodeURI(
    `https://res.cloudinary.com/${cloudName}/image/upload/${serializeTransformations(merged)}/${publicId}`
  );
}

export function buildCloudinarySrc(
  cloudName: string,
  publicId: string,
  transformations?: CloudinaryTransformations | string
): string {
  return buildCloudinaryImageUrl(cloudName, publicId, fallbackSrcWidth, transformations);
}

export function buildCloudinarySrcSet(
  cloudName: string,
  publicId: string,
  transformations?: CloudinaryTransformations | string
): string {
  return cloudinarySrcSetWidths
    .map((width) => `${buildCloudinaryImageUrl(cloudName, publicId, width, transformations)} ${width}w`)
    .join(', ');
}
